import React, {useCallback, useRef} from 'react';
import clsx from 'clsx';
import type {AttributeId, Permission} from '@netx/core-asset/models/attribute';
import {isEmptyValue} from '@netx/core-components/Form/utils';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {compareProps} from '@netx/core-next/utils/react';
import {AttributeSearch} from '../../components/Action';
import {
	LegacyFormAttribute,
	type LegacyFormAttributeProps,
} from '../../components/Attribute';
import {
	ATTRIBUTE_CLASS_NAME,
	attributeClasses,
	type AttributeClasses,
	getAttributeUtilityClass,
} from './attributeClasses';

export type LegacyFormAttributeContainerState = WithClasses<
	AttributeClasses,
	{
		limitHeightToContainer?: boolean;
		matchContainerHeight?: boolean;
	}
>;

const useUtilityClasses = (ownerState: LegacyFormAttributeContainerState) => {
	const {classes, limitHeightToContainer, matchContainerHeight} = ownerState;
	const slots = {
		root: [
			'root',
			limitHeightToContainer && 'limitHeightToContainer',
			matchContainerHeight && 'matchContainerHeight',
		],
		leadingControls: ['leadingControls'],
	};
	return composeClasses(slots, getAttributeUtilityClass, classes);
};

const AttributeRoot = styled('div', {
	name: ATTRIBUTE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'inline-flex',
	width: '100%',
	[`&.${attributeClasses.limitHeightToContainer}`]: {
		maxHeight: '100%',
		height: '100%',
	},
	[`&.${attributeClasses.matchContainerHeight}`]: {
		height: '100%',
	},
}));

const AttributeLeadingControls = styled('div', {
	name: ATTRIBUTE_CLASS_NAME,
	slot: 'LeadingControls',
})(({theme}) => ({
	display: 'inline-flex',
	flex: '0 0 auto',
	height: 'max-content',
	marginTop: theme.spacing(1),
	'& > *': {
		marginRight: theme.spacing(1),
	},
}));

export interface LegacyFormAttributeContainerProps
	extends Omit<LegacyFormAttributeProps, 'onSearch' | 'classes'>,
		WithClasses<
			AttributeClasses,
			{
				onSearch?: (id: AttributeId, value: any) => void;
				permission?: Permission;
				// This currently is only supported properly for autocomplets (tags/multiselect/select)
				limitHeightToContainer?: boolean;
				matchContainerHeight?: boolean;
				alignActions?: 'top' | 'bottom' | 'center';
			}
		> {}

export const LegacyFormAttributeContainerPrimitive = ({
	classes: _classes,
	className,
	alignActions,
	limitHeightToContainer,
	matchContainerHeight,
	permission,
	onSearch,
	...props
}: LegacyFormAttributeContainerProps) => {
	const {attrData, disabled, loadingValue, readOnly, value} = props;

	permission = permission || attrData.permission;

	const isReadOnly = !props.form || readOnly || permission === 'r';

	const ownerState: LegacyFormAttributeContainerState = {
		classes: _classes,
		className,
		limitHeightToContainer,
		matchContainerHeight,
	};

	const classes = useUtilityClasses(ownerState);

	const valueRef = useRef(value);
	valueRef.current = value;

	const handleValueSearch = useCallback(() => {
		onSearch?.(attrData.attributeid, valueRef.current);
	}, [attrData.attributeid, onSearch]);

	const handleValueItemSearch = useCallback(
		(value: string) => {
			onSearch?.(attrData.attributeid, value);
		},
		[attrData.attributeid, onSearch],
	);

	return (
		<AttributeRoot className={clsx(classes.root, className)}>
			<AttributeLeadingControls className={classes.leadingControls}>
				{onSearch ? (
					<AttributeSearch
						disabled={disabled || loadingValue || isEmptyValue(value)}
						onClick={handleValueSearch}
					/>
				) : null}
			</AttributeLeadingControls>
			<LegacyFormAttribute
				{...props}
				readOnly={isReadOnly}
				onSearch={onSearch ? handleValueItemSearch : undefined}
			/>
		</AttributeRoot>
	);
};

export const LegacyFormAttributeContainer = React.memo(
	LegacyFormAttributeContainerPrimitive,
	compareProps(),
);

export default LegacyFormAttributeContainer;
